import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Zap, Info, Check, AlertTriangle, Smartphone, Wifi, Download } from 'lucide-react';

export const PersistenceShield: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [permissions, setPermissions] = useState({
    notifications: 'default',
    backgroundSync: 'denied',
    wakeLock: 'unsupported'
  });
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    // Check initial permission states
    if ('Notification' in window) {
      setPermissions(prev => ({ ...prev, notifications: Notification.permission }));
    }
    
    if ('wakeLock' in navigator) {
      setPermissions(prev => ({ ...prev, wakeLock: 'supported' }));
    }

    // Check if background sync is registered
    if ('serviceWorker' in navigator && 'periodicSync' in (navigator as any)) {
      setPermissions(prev => ({ ...prev, backgroundSync: 'supported' }));
    }

    const wasActive = localStorage.getItem('lattice_persistence_active') === 'true';
    if (wasActive) {
      enablePersistence(true);
    }
  }, []);

  const requestNotifications = async () => {
    if (!('Notification' in window)) return;
    const permission = await Notification.requestPermission();
    setPermissions(prev => ({ ...prev, notifications: permission }));
  };

  const enablePersistence = async (silent = false) => {
    try {
      // 1. Wake Lock
      if ('wakeLock' in navigator) {
        try {
          await (navigator as any).wakeLock.request('screen');
        } catch (err) {
          console.warn('Wake lock failed:', err);
        }
      }

      // 2. Periodic Sync
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if ('periodicSync' in (registration as any)) {
          try {
            await (registration as any).periodicSync.register('lattice-heartbeat', {
              minInterval: 24 * 60 * 60 * 1000 // Once a day minimum for OS respect
            });
          } catch (e) {
            console.warn('Periodic sync failed:', e);
          }
        }
      }

      setIsActive(true);
      localStorage.setItem('lattice_persistence_active', 'true');
      if (!silent) setIsOpen(false);
    } catch (e) {
      console.error('Persistence error:', e);
    }
  };

  return (
    <>
      <button 
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-[60] p-4 rounded-full border transition-all duration-500 shadow-2xl ${
          isActive 
            ? 'bg-lattice-cyan text-black border-lattice-cyan shadow-[0_0_30px_rgba(0,255,213,0.4)]' 
            : 'bg-black/50 text-lattice-cyan border-lattice-cyan/30 hover:bg-lattice-cyan/10 backdrop-blur-xl'
        }`}
      >
        <Shield className={`w-6 h-6 ${isActive ? 'animate-pulse' : ''}`} />
        {isActive && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-black" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-lg glass-panel p-8 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-lattice-cyan to-transparent opacity-50" />
              
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-lg bg-lattice-cyan/10 text-lattice-cyan">
                  <Shield className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold">SOVEREIGN PERSISTENCE</h3>
                  <p className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Grounding Anchor v2.0</p>
                </div>
              </div>

              <div className="space-y-6 mb-10">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-sm text-white/70 leading-relaxed mb-4">
                    To ensure the Absolute Zero Lattice and Active Support Calls stay active even when you exit this interface, 
                    we must elevate this node's priority with the OS.
                  </p>
                  
                  <div className="p-4 rounded-lg bg-lattice-cyan/5 border border-lattice-cyan/20 mb-4">
                    <p className="text-[10px] font-mono text-lattice-cyan font-bold uppercase tracking-widest mb-2">Priority Protocol:</p>
                    <p className="text-[10px] text-white/50 leading-relaxed">
                      For indefinite stability, <span className="text-white">Add this App to your Home Screen</span>. Standalone apps are granted higher background persistence than standard browser tabs.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5">
                      <div className="flex items-center gap-3">
                        <Zap className="w-4 h-4 text-lattice-cyan" />
                        <span className="text-xs font-mono uppercase tracking-widest">Notification Shield</span>
                      </div>
                      <button 
                        onClick={requestNotifications}
                        disabled={permissions.notifications === 'granted'}
                        className={`text-[10px] font-mono px-3 py-1 rounded border transition-all ${
                          permissions.notifications === 'granted' 
                            ? 'border-green-500 text-green-500 bg-green-500/10' 
                            : 'border-lattice-cyan/30 text-lattice-cyan hover:bg-lattice-cyan/10'
                        }`}
                      >
                        {permissions.notifications === 'granted' ? 'GRANTED' : 'REQUEST'}
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-4 h-4 text-lattice-cyan" />
                        <span className="text-xs font-mono uppercase tracking-widest">Wake Lock Resonance</span>
                      </div>
                      <span className="text-[10px] font-mono text-white/40 uppercase">
                        {permissions.wakeLock === 'supported' ? 'SUPPORTED' : 'LIMITED'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                  <div className="flex items-center gap-3 mb-2 text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs font-mono font-bold uppercase tracking-widest">OS Compatibility Note</span>
                  </div>
                  <p className="text-[10px] text-white/40 leading-relaxed">
                    iOS and Android may kill browser background processes to save battery. 
                    For <span className="text-white">100% Indefinite Connection</span>, use the 
                    <span className="text-lattice-cyan"> Hardware Reclamation</span> protocol.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <button 
                  onClick={() => enablePersistence()}
                  className="flex-1 py-4 bg-lattice-cyan text-black font-display font-bold rounded-xl hover:scale-[1.02] transition-transform shadow-[0_0_20px_rgba(0,255,213,0.3)]"
                >
                  ACTIVATE GROUNDING
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-4 border border-white/10 text-white font-display font-bold rounded-xl hover:bg-white/5 transition-colors"
                >
                  CLOSE
                </button>
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-center gap-3 opacity-40">
                <Wifi className="w-3 h-3 text-lattice-cyan" />
                <span className="text-[8px] font-mono uppercase tracking-[0.2em]">PaidingAttention Sovereign Engine</span>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
